import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
  type Signal,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { Money } from '@principle-theorem/accounting';
import { InvoiceActionService } from '@principle-theorem/ng-principle-accounting';
import { CurrentBrandScope } from '@principle-theorem/ng-principle-shared';
import {
  isDisabled$,
  NgCommonModule,
  TypedFormControl,
} from '@principle-theorem/ng-shared';
import {
  AccountCredit,
  stafferToNamedDoc,
} from '@principle-theorem/principle-core';
import {
  AccountCreditType,
  type ITreatmentCategory,
} from '@principle-theorem/principle-core/interfaces';
import { snapshot, type DocumentReference } from '@principle-theorem/shared';
import { type Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import {
  AccountCreditCapture,
  AccountCreditCaptureStatus,
} from '../add-account-credit-dialog/account-credit-capture';
import {
  type AppointmentDepositDialogResult,
  type IAddAccountCreditDialogData,
  type IAppointmentDepositContext,
} from '../add-account-credit-dialog/add-account-credit-dialog.component';
import {
  AccountCreditFormGroup,
  type ICreditOptionGroup,
} from '../add-account-credit-dialog/add-account-credit-form';
import { isResolvedTransactionOption } from '../transaction-providers/transaction-provider';
import { HICAPS_CONNECT_PAYMENT_PROVIDER_OPTION } from '../transaction-providers/transaction-provider-options/hicaps-connect-transaction-provider-options';
import { MEDIPASS_VIRTUAL_TERMINAL_PROVIDER_OPTION } from '../transaction-providers/transaction-provider-options/medipass-transaction-provider-options';
import {
  CASH_PROVIDER_OPTION,
  MANUAL_PROVIDER_OPTION,
  STRIPE_PROVIDER_OPTION,
} from '../transaction-providers/transaction-provider-options/transaction-provider-options';
import { TYRO_PURCHASE_PROVIDER_OPTION } from '../transaction-providers/transaction-provider-options/tyro-transaction-provider-options';
import { TransactionProviders } from '../transaction-providers/transaction-providers.service';

export interface IAppointmentDepositCaptureData
  extends IAddAccountCreditDialogData {
  depositContext: IAppointmentDepositContext;
}

/**
 * Inline host for the appointment deposit capture step.
 *
 * This is the sidebar-resident counterpart to the account-credit dialog's
 * deposit branch. Capture was de-dialogued because a dismissable dialog
 * abandons a claimed capture, so the amount and payment method are collected in
 * a panel the operator cannot close by clicking away, with an explicit back
 * action that is only offered while nothing irreversible has happened.
 *
 * The reservation targets (practitioner, treatment category, appointment) are
 * fixed by the appointment that owns the deposit, so they are carried through
 * from the deposit context rather than re-picked here.
 */
@Component({
  selector: 'pr-appointment-deposit-capture',
  templateUrl: './appointment-deposit-capture.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgCommonModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSelectModule,
  ],
})
export class AppointmentDepositCaptureComponent {
  private readonly _transactions = inject(TransactionProviders);
  private readonly _invoiceActions = inject(InvoiceActionService);
  readonly depositsEnabled = toSignal(
    inject(CurrentBrandScope).depositsEnabled$,
    {
      initialValue: false,
    }
  );

  readonly data = input.required<IAppointmentDepositCaptureData>();
  readonly captured = output<AppointmentDepositDialogResult>();
  readonly back = output<void>();

  readonly capturing = signal(false);
  readonly form: Signal<AccountCreditFormGroup> = computed(() =>
    AppointmentDepositCaptureComponent._buildForm(this.data())
  );
  readonly optionGroups$: Observable<ICreditOptionGroup[]>;
  readonly submitDisabled$: Observable<boolean>;

  constructor() {
    this.optionGroups$ = this._transactions
      .resolveProviders$([
        CASH_PROVIDER_OPTION,
        MANUAL_PROVIDER_OPTION,
        MEDIPASS_VIRTUAL_TERMINAL_PROVIDER_OPTION,
        STRIPE_PROVIDER_OPTION,
        TYRO_PURCHASE_PROVIDER_OPTION,
        HICAPS_CONNECT_PAYMENT_PROVIDER_OPTION,
      ])
      .pipe(map((options) => [{ label: 'Deposit', options }]));

    this.submitDisabled$ = toObservable(this.form).pipe(
      switchMap((form) => isDisabled$(form))
    );
  }

  private static _buildForm(
    data: IAppointmentDepositCaptureData
  ): AccountCreditFormGroup {
    const context = data.depositContext;
    const form = new AccountCreditFormGroup(
      new TypedFormControl<DocumentReference<ITreatmentCategory>>()
    );
    form.patchValue({
      description: context.defaultDescription,
      amount: Money.fromCents(context.amountCents),
      reservedFor: {
        practitioner: context.practitioner,
        treatmentCategory: context.treatmentCategoryRef,
        appointmentRef: context.appointmentRef,
      },
    });
    if (!context.canOverrideAmount) {
      form.controls.amount.disable();
    }
    return form;
  }

  goBack(): void {
    if (this.capturing()) {
      return;
    }
    this.back.emit();
  }

  /**
   * Runs the same claim-then-capture sequence the account-credit dialog runs,
   * and reports its outcome through `captured` in place of a dialog close so
   * the caller's finalisation ordering is unchanged.
   */
  async submit(): Promise<void> {
    const form = this.form();
    if (!form.valid || this.capturing()) {
      return;
    }
    const formData = form.getRawValue();
    const resolvedMethod = isResolvedTransactionOption(formData.method)
      ? formData.method
      : undefined;
    if (!resolvedMethod) {
      return;
    }

    const data = this.data();
    const credit = AccountCredit.init({
      type: AccountCreditType.Deposit,
      description: formData.description,
      amount: formData.amount,
      reservedFor: {
        practitioner: formData.reservedFor.practitioner
          ? stafferToNamedDoc(formData.reservedFor.practitioner)
          : undefined,
        treatment: formData.reservedFor.treatment,
        appointmentRef: formData.reservedFor.appointmentRef,
        treatmentCategory: formData.reservedFor.treatmentCategory,
      },
      practiceRef: data.practice.ref,
    });

    this.capturing.set(true);
    try {
      const outcome = await AccountCreditCapture.capture({
        option: resolvedMethod,
        credit,
        patient: data.patient,
        practice: data.practice,
        staffer: await snapshot(this._invoiceActions.staffer$),
        transactions: this._transactions,
        acquireInvoiceClaim: data.depositContext.acquireInvoiceClaim,
        abandonInvoiceClaim: data.depositContext.abandonInvoiceClaim,
      });
      // Anything short of a capture leaves the operator on this step to pick
      // another method. The provider has already said why it stopped, and an
      // abandoned attempt has released its claim so the retry can take one.
      if (outcome.status !== AccountCreditCaptureStatus.Captured) {
        return;
      }
      this.captured.emit({
        invoice: outcome.invoice,
        transaction: outcome.transaction,
        amountCents: Money.toCents(formData.amount),
      });
    } catch (error) {
      this.captured.emit({ status: 'error', error });
    } finally {
      this.capturing.set(false);
    }
  }
}
